'use client';

import { useState, useEffect, useRef, useCallback, type Dispatch, type SetStateAction } from 'react';
import { useTheme } from 'next-themes';
import {
  Medal,
  Sparkle,
  CheckCircle,
  Info,
  ArrowLeft,
  ArrowRight,
  Scales,
  Fire,
  Barbell,
  Drop,
  Leaf,
  ShoppingCart,
  SpinnerGap,
  Crown,
  Star,
  Diamond,
  CaretRight,
} from '@phosphor-icons/react';
import { ThemedImage } from '@/components/ui/themed-image';
import { cn } from '@/lib/utils';
import { type ProductWithCategories } from '@/lib/api/products';
import { useStatusRolls } from '@/hooks/use-status-rolls';
import { useMoneyRules, getStatusRollRule, type StatusRollType } from '@/lib/money-rules';
import { PromotionModal } from './promotion-modal';

// ========== TYPES & CONFIG ==========

interface BonusRoll {
  id: number;
  name: string;
  description: string;
  image_url: string;
  price: number;
  weight: number;
  ingredients?: string;
  calories?: number;
  proteins?: number;
  fats?: number;
  carbs?: number;
  threshold: number;
  type: 'silver' | 'gold' | 'platinum';
  label: string;
  color: string;
  bgColor: string;
  darkBgColor: string;
  icon: React.ElementType;
}

// Пороги статусных роллов здесь БОЛЬШЕ НЕ ЗАШИТЫ: они приходят с сервера
// (GET /api/money-rules) и правятся в админке, раздел «Деньги». Своя копия
// 2500/3500/4500 в этом файле означала бы, что после правки порога модалка
// продолжает обещать старую сумму, а заказ считается по новой.

const TIER_CONFIG = {
  silver: {
    label: 'Silver',
    color: 'var(--bonus-silver)',
    bgColor: 'var(--surface-secondary)',
    darkBgColor: 'color-mix(in srgb, var(--bonus-silver) 10%, transparent)',
    icon: Star,
  },
  gold: {
    label: 'Gold',
    color: 'var(--bonus-gold)',
    bgColor: 'var(--surface-primary)',
    darkBgColor: 'color-mix(in srgb, var(--bonus-gold) 10%, transparent)',
    icon: Crown,
  },
  platinum: {
    label: 'Platinum',
    color: 'var(--bonus-platinum)',
    bgColor: 'var(--violet-50)',
    darkBgColor: 'color-mix(in srgb, var(--bonus-platinum) 10%, transparent)',
    icon: Diamond,
  },
};

// ========== ROLL CAROUSEL ==========

function RollCarousel({
  rolls,
  activeIndex,
  onActiveChange,
  isDark,
}: {
  rolls: BonusRoll[];
  activeIndex: number;
  onActiveChange: (index: number) => void;
  isDark: boolean;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);

  const scrollToIndex = useCallback(
    (index: number, smooth = true) => {
      const container = scrollRef.current;
      if (!container) return;
      const children = container.children;
      if (!children[index]) return;

      const child = children[index] as HTMLElement;
      const scrollLeft =
        child.offsetLeft - container.offsetWidth / 2 + child.offsetWidth / 2;

      isScrollingRef.current = true;
      container.scrollTo({
        left: scrollLeft,
        behavior: smooth ? 'smooth' : 'instant',
      });

      setTimeout(() => {
        isScrollingRef.current = false;
      }, 400);
    },
    [],
  );

  useEffect(() => {
    scrollToIndex(activeIndex, false);
  }, []);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let scrollTimeout: ReturnType<typeof setTimeout>;

    const handleScroll = () => {
      if (isScrollingRef.current) return;

      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const containerCenter = container.scrollLeft + container.offsetWidth / 2;
        let closestIndex = 0;
        let closestDistance = Infinity;

        Array.from(container.children).forEach((child, i) => {
          const el = child as HTMLElement;
          const childCenter = el.offsetLeft + el.offsetWidth / 2;
          const distance = Math.abs(containerCenter - childCenter);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = i;
          }
        });

        if (closestIndex !== activeIndex) {
          onActiveChange(closestIndex);
        }
      }, 80);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimeout);
    };
  }, [activeIndex, onActiveChange]);

  const canPrev = activeIndex > 0;
  const canNext = activeIndex < rolls.length - 1;

  return (
    <div className="relative">
      {/* Arrows */}
      {canPrev && (
        <button
          onClick={() => {
            const next = activeIndex - 1;
            onActiveChange(next);
            scrollToIndex(next);
          }}
          className="absolute left-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-pill bg-[var(--surface-primary)]/90 backdrop-blur-sm border border-[var(--border-default)] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--primary)] transition-colors shadow-raised"
        >
          <ArrowLeft className="w-4 h-4" weight="bold" />
        </button>
      )}
      {canNext && (
        <button
          onClick={() => {
            const next = activeIndex + 1;
            onActiveChange(next);
            scrollToIndex(next);
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-pill bg-[var(--surface-primary)]/90 backdrop-blur-sm border border-[var(--border-default)] flex items-center justify-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--primary)] transition-colors shadow-raised"
        >
          <ArrowRight className="w-4 h-4" weight="bold" />
        </button>
      )}

      {/* Edge fades */}
      <div className="absolute inset-y-0 left-0 w-12 bg-gradient-to-r from-[var(--bg-elevated)] to-transparent z-[5] pointer-events-none" />
      <div className="absolute inset-y-0 right-0 w-12 bg-gradient-to-l from-[var(--bg-elevated)] to-transparent z-[5] pointer-events-none" />

      {/* Scrollable track */}
      <div
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto px-12 py-4 snap-x snap-mandatory scrollbar-hide"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {rolls.map((roll, i) => {
          const isActive = i === activeIndex;
          const TierIcon = roll.icon;
          return (
            <button
              key={roll.id}
              onClick={() => {
                onActiveChange(i);
                scrollToIndex(i);
              }}
              className={cn(
                'shrink-0 flex flex-col items-center gap-2 p-3 rounded-card border snap-center',
                'transition-all duration-300 w-[130px]',
                // Класса тени здесь НЕТ намеренно: ниже в style стоит
                // boxShadow цвета уровня, а inline-стиль перебивает любой
                // класс. Поставить сюда shadow-* значило бы добавить мёртвую
                // запись, которая ничего не рисует. Цветной ореол уровня —
                // это осознанная краска, а не системная глубина.
                isActive
                  ? 'scale-105'
                  : 'border-[var(--border-default)] bg-[var(--surface-secondary)] hover:border-[var(--primary)]/50',
              )}
              style={
                isActive
                  ? {
                      borderColor: roll.color,
                      backgroundColor: isDark ? roll.darkBgColor : roll.bgColor,
                      boxShadow: `0 8px 24px ${roll.color}25`,
                    }
                  : undefined
              }
            >
              <div
                className="relative w-20 h-20 rounded-card overflow-hidden"
              >
                {roll.image_url ? (
                  <ThemedImage
                    src={roll.image_url}
                    alt={roll.name}
                    fill
                    className="object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-[var(--surface)]">
                    <TierIcon className="w-6 h-6 text-[var(--text-tertiary)]" />
                  </div>
                )}
              </div>
              {/* Tier badge */}
              <span
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-pill t-nano font-bold uppercase tracking-wider',
                )}
                style={{
                  backgroundColor: isActive
                    ? roll.color
                    : isDark
                      ? roll.darkBgColor
                      : roll.bgColor,
                  color: isActive ? 'white' : roll.color,
                }}
              >
                <TierIcon className="w-3 h-3" weight="fill" />
                {roll.label}
              </span>
              <p
                className={cn(
                  'text-xs font-medium text-center leading-tight line-clamp-2 w-full transition-colors duration-300',
                )}
                style={{ color: isActive ? roll.color : 'var(--text-secondary)' }}
              >
                {roll.name}
              </p>
            </button>
          );
        })}
      </div>

      {/* Tier-colored dot indicators */}
      <div className="flex justify-center gap-2 pb-2">
        {rolls.map((roll, i) => (
          <div
            key={i}
            className="h-1.5 rounded-pill transition-all duration-300"
            style={{
              width: i === activeIndex ? 24 : 6,
              backgroundColor: i === activeIndex ? roll.color : 'var(--text-disabled)',
            }}
          />
        ))}
      </div>
    </div>
  );
}

// ========== DESCRIPTION BLOCK ==========

const INFO_KEYWORDS = [
  'возможно попадание',
  'рекомендуется употребить',
  'хранить при',
  'не является горячим',
  'запекание',
  'срок годности',
  'условия хранения',
  'температур',
];

function DescriptionBlock({
  text,
  accentColor,
  isDark,
}: {
  text: string;
  accentColor: string;
  isDark: boolean;
}) {
  // Split by double newlines into paragraphs, preserve single \n within paragraphs
  const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim());
  const marketing: string[] = [];
  const info: string[] = [];

  for (const p of paragraphs) {
    const lower = p.toLowerCase();
    if (INFO_KEYWORDS.some((kw) => lower.includes(kw))) {
      info.push(p.trim());
    } else {
      marketing.push(p.trim());
    }
  }

  return (
    <div className="space-y-3">
      {/* Marketing paragraphs */}
      {marketing.length > 0 && (
        <div className="space-y-2">
          {marketing.map((para, i) => (
            <p key={i} className="text-sm text-[var(--text-secondary)] leading-relaxed">
              {para}
            </p>
          ))}
        </div>
      )}

      {/* Info notices */}
      {info.length > 0 && (
        <div
          className="rounded-card p-3 flex gap-2.5"
          style={{
            backgroundColor: isDark
              ? 'color-mix(in srgb, var(--text-tertiary) 8%, transparent)'
              : 'var(--surface-secondary)',
          }}
        >
          <Info className="w-4 h-4 text-[var(--text-tertiary)] flex-shrink-0 mt-0.5" weight="fill" />
          <div className="space-y-1.5">
            {info.map((line, i) => (
              <p key={i} className="text-xs text-[var(--text-tertiary)] leading-relaxed">
                {line}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ========== NUTRITION BLOCK ==========

function NutritionBlock({
  calories,
  proteins,
  fats,
  carbs,
  weight,
}: {
  calories?: number;
  proteins?: number;
  fats?: number;
  carbs?: number;
  weight?: number;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="py-4">
      <button
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'w-full flex items-center justify-between gap-3 p-3.5 rounded-card transition-all duration-200',
          open
            ? 'bg-[var(--bg-elevated)] ring-1 ring-[var(--primary)]/20'
            : 'bg-[var(--bg-elevated)]',
        )}
      >
        <div className="flex items-center gap-3">
          <div
            className={cn(
              'flex items-center justify-center w-9 h-9 rounded-card transition-colors',
              open ? 'bg-[var(--primary)] text-white' : 'bg-[var(--primary-subtle)] text-[var(--primary)]',
            )}
          >
            <Fire weight="fill" className="w-5 h-5" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-[var(--text-primary)]">Пищевая ценность</p>
            <p className="text-xs text-[var(--text-tertiary)]">
              {calories ? `${calories} ккал` : ''}
              {weight ? ` · ${weight}` : ''}
            </p>
          </div>
        </div>
        <CaretRight
          weight="bold"
          className={cn(
            'w-4 h-4 text-[var(--text-tertiary)] transition-transform duration-200',
            open && 'rotate-90',
          )}
        />
      </button>

      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]',
          open ? 'max-h-[300px] opacity-100 mt-2' : 'max-h-0 opacity-0 mt-0',
        )}
      >
        <div className="rounded-card bg-[var(--bg-elevated)] p-4 space-y-2.5">
          {calories != null && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2.5">
                <Fire weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Калории
              </span>
              <span className="text-sm font-semibold text-[var(--text-primary)]">{calories} ккал</span>
            </div>
          )}
          {proteins != null && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2.5">
                <Barbell weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Белки
              </span>
              <span className="text-sm font-semibold text-[var(--text-primary)]">{proteins} г</span>
            </div>
          )}
          {fats != null && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2.5">
                <Drop weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Жиры
              </span>
              <span className="text-sm font-semibold text-[var(--text-primary)]">{fats} г</span>
            </div>
          )}
          {carbs != null && (
            <div className="flex items-center justify-between">
              <span className="text-sm text-[var(--text-secondary)] flex items-center gap-2.5">
                <Leaf weight="fill" className="w-4 h-4 text-[var(--primary)]" /> Углеводы
              </span>
              <span className="text-sm font-semibold text-[var(--text-primary)]">{carbs} г</span>
            </div>
          )}
          {weight != null && (
            <>
              <div className="h-px bg-[var(--border-default)]" />
              <div className="flex items-center justify-between">
                <span className="text-sm text-[var(--text-secondary)]">Вес</span>
                <span className="text-sm font-semibold text-[var(--text-primary)]">{weight}</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ========== ROLL DETAILS ==========

function RollDetails({ roll, isDark }: { roll: BonusRoll; isDark: boolean }) {
  const tier = TIER_CONFIG[roll.type];
  const TierIcon = tier.icon;

  const hasNutrition = roll.calories || roll.proteins || roll.fats || roll.carbs;

  return (
    <div className="px-4 md:px-6">
      {/* Image */}
      <div className="relative w-full aspect-[16/10] rounded-card overflow-hidden bg-[var(--surface)]">
        {roll.image_url ? (
          <ThemedImage
            src={roll.image_url}
            alt={roll.name}
            fill
            className="object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TierIcon className="w-12 h-12 text-[var(--text-tertiary)]" />
          </div>
        )}
        {/* Tier badge */}
        <div className="absolute top-3 left-3">
          <span
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill text-xs font-bold uppercase tracking-wider text-white shadow-raised backdrop-blur-sm"
            style={{ backgroundColor: `${roll.color}dd` }}
          >
            <TierIcon className="w-3.5 h-3.5" weight="fill" />
            {roll.label}
          </span>
        </div>
        {/* Threshold badge */}
        <div className="absolute top-3 right-3">
          <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-pill text-xs font-bold text-white bg-black/50 backdrop-blur-sm">
            От {formatPrice(roll.threshold)}
          </span>
        </div>
      </div>

      {/* Name + description */}
      <div className="py-4">
        <h4 className="text-base md:text-lg font-bold text-[var(--text-primary)] leading-tight mb-2">
          {roll.name}
        </h4>
        {roll.description && <DescriptionBlock text={roll.description} accentColor={roll.color} isDark={isDark} />}
      </div>

      <hr className="border-[var(--border-default)]" />

      {/* Weight */}
      <div className="py-4">
        <div className="flex items-center gap-3 p-3.5 rounded-card bg-[var(--bg-elevated)]">
          <div className="flex items-center justify-center w-9 h-9 rounded-card bg-[var(--primary-subtle)]">
            <Scales weight="fill" className="w-5 h-5 text-[var(--primary)]" />
          </div>
          <div>
            <p className="text-xs text-[var(--text-tertiary)]">Масса</p>
            <p className="text-sm font-bold text-[var(--text-primary)]">{roll.weight}</p>
          </div>
        </div>
      </div>

      <hr className="border-[var(--border-default)]" />

      {/* Ingredients */}
      {roll.ingredients && (
        <>
          <div className="py-4">
            <p
              className="t-micro font-semibold uppercase tracking-wider mb-2.5"
              style={{ color: roll.color }}
            >
              Состав
            </p>
            <div className="flex flex-wrap gap-1.5">
              {roll.ingredients.split(',').map((ingredient, idx) => (
                <span
                  key={idx}
                  className="px-2.5 py-1 rounded-pill text-xs font-medium"
                  style={{
                    backgroundColor: isDark ? roll.darkBgColor : roll.bgColor,
                    color: 'var(--text-primary)',
                  }}
                >
                  {ingredient.trim()}
                </span>
              ))}
            </div>
          </div>
          <hr className="border-[var(--border-default)]" />
        </>
      )}

      {/* Nutrition (КБЖУ) — expandable */}
      {hasNutrition && (
        <NutritionBlock
          calories={roll.calories}
          proteins={roll.proteins}
          fats={roll.fats}
          carbs={roll.carbs}
          weight={roll.weight}
        />
      )}
    </div>
  );
}

// ========== HELPERS ==========

function formatPrice(price: number) {
  return Math.round(price).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' ₽';
}

// ========== MAIN MODAL ==========

interface StatusRollsModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Условия акции с сервера (site_promotion_terms). Пусто или не передали —
   * показываем зашитый текст: пустой блок условий у публичной оферты хуже,
   * чем устаревший.
   */
  terms?: readonly string[];
}

/** Условия на 16.08.2026 — запасной текст, если сервер не ответил. */
const FALLBACK_TERMS = [
  'Статусные роллы добавляются автоматически при достижении суммы',
  'Silver ролл — при заказе от 2 500 ₽',
  'Gold ролл — при заказе от 3 500 ₽',
  'Platinum ролл — при заказе от 4 500 ₽',
  'Получаете только один статусный ролл — самый ценный по сумме заказа',
  'Акция суммируется с другими акциями',
  // Категории 7 (соусы) и 15 («Роллы -30%») исключены из суммы в самом расчёте
  // сервера (azia_back: routers/orders.js, EXCLUDED_CATEGORY_IDS). Запасной
  // текст обязан повторять site_promotion_terms, иначе при недоступном бэкенде
  // оферта обещает больше, чем сервер считает.
  'Учитываются все товары кроме соусов, товаров из категории «Роллы -30%» и товаров из рулетки',
  'Стоимость доставки, приборов и соусов не включается в сумму для статуса',
];

export function StatusRollsModal({ open, onClose, terms }: StatusRollsModalProps) {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [activeIndex, setActiveIndex] = useState(0);
  const [bonusRolls, setBonusRolls] = useState<BonusRoll[]>([]);
  const { rolls: rollProducts, isLoading } = useStatusRolls();
  const silverRes = rollProducts.silver ?? null;
  const goldRes = rollProducts.gold ?? null;
  const platinumRes = rollProducts.platinum ?? null;

  // Пороги и id подарочных товаров — с сервера, единственным источником
  // (см. @/lib/money-rules). До ответа сервера действуют значения по
  // умолчанию, совпадающие с зашитыми в бэкенде.
  const moneyRules = useMoneyRules();
  const ruleFor = useCallback(
    (type: StatusRollType) => getStatusRollRule(type, moneyRules),
    [moneyRules],
  );

  useEffect(() => {
    if (!open) return;
    setActiveIndex(0);

    const fetchRolls = () => {
      try {

        const mapToRoll = (
          res: ProductWithCategories | null,
          type: 'silver' | 'gold' | 'platinum',
        ): BonusRoll | null => {
          if (!res) return null;
          const cfg = TIER_CONFIG[type];
          return {
            id: res.id,
            name: res.name,
            description: res.description || '',
            image_url: res.image_url,
            price: res.price,
            weight: res.weight || (type === 'silver' ? 200 : type === 'gold' ? 230 : 260),
            ingredients: res.ingredients,
            calories: res.calories,
            proteins: res.proteins,
            fats: res.fats,
            carbs: res.carbs,
            threshold: ruleFor(type).min_sum,
            type,
            label: cfg.label,
            color: cfg.color,
            bgColor: cfg.bgColor,
            darkBgColor: cfg.darkBgColor,
            icon: cfg.icon,
          };
        };

        const rolls = [
          mapToRoll(silverRes, 'silver'),
          mapToRoll(goldRes, 'gold'),
          mapToRoll(platinumRes, 'platinum'),
        ].filter((r): r is BonusRoll => r !== null);

        setBonusRolls(
          rolls.length > 0
            ? rolls
            : [
                {
                  id: ruleFor('silver').product_id,
                  name: 'Silver Ролл',
                  description: 'Статусный ролл Silver',
                  image_url: '/placeholder-roll.jpg',
                  price: 350,
                  weight: 200,
                  ingredients: 'Огурец, сыр, авокадо',
                  threshold: ruleFor('silver').min_sum,
                  type: 'silver',
                  label: 'Silver',
                  color: TIER_CONFIG.silver.color,
                  bgColor: TIER_CONFIG.silver.bgColor,
                  darkBgColor: TIER_CONFIG.silver.darkBgColor,
                  icon: TIER_CONFIG.silver.icon,
                },
                {
                  id: ruleFor('gold').product_id,
                  name: 'Gold Ролл',
                  description: 'Статусный ролл Gold',
                  image_url: '/placeholder-roll.jpg',
                  price: 450,
                  weight: 230,
                  ingredients: 'Лосось, сыр, авокадо',
                  threshold: ruleFor('gold').min_sum,
                  type: 'gold',
                  label: 'Gold',
                  color: TIER_CONFIG.gold.color,
                  bgColor: TIER_CONFIG.gold.bgColor,
                  darkBgColor: TIER_CONFIG.gold.darkBgColor,
                  icon: TIER_CONFIG.gold.icon,
                },
                {
                  id: ruleFor('platinum').product_id,
                  name: 'Platinum Ролл',
                  description: 'Статусный ролл Platinum',
                  image_url: '/placeholder-roll.jpg',
                  price: 550,
                  weight: 260,
                  ingredients: 'Тунец, лосось, сыр, икра',
                  threshold: ruleFor('platinum').min_sum,
                  type: 'platinum',
                  label: 'Platinum',
                  color: TIER_CONFIG.platinum.color,
                  bgColor: TIER_CONFIG.platinum.bgColor,
                  darkBgColor: TIER_CONFIG.platinum.darkBgColor,
                  icon: TIER_CONFIG.platinum.icon,
                },
              ],
        );
      } catch (err) {
        console.error('Ошибка при загрузке роллов:', err);
      }
    };

    fetchRolls();
  }, [open, silverRes, goldRes, platinumRes, ruleFor]);

  const activeRoll = bonusRolls[activeIndex] ?? null;

  return (
    <PromotionModal
      open={open}
      onClose={onClose}
      title="Статусные роллы"
      icon={<Medal className="w-5 h-5 md:w-6 md:h-6 text-white" weight="fill" />}
    >
      <div className="space-y-6 md:space-y-8 pt-5 pb-6">
        {/* Hero banner */}
        <div className="mx-4 md:mx-6 p-4 md:p-6 rounded-card bg-gradient-to-br from-[var(--primary-subtle)] to-[var(--primary-muted)] border border-[var(--primary)]/20">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-card bg-[var(--primary)] flex items-center justify-center flex-shrink-0">
              <Sparkle className="w-5 h-5 text-white" weight="fill" />
            </div>
            <div className="flex-1">
              <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)] mb-1">
                Бесплатные роллы к заказу!
              </h3>
              <p className="text-sm text-[var(--text-secondary)] leading-relaxed">
                Соберите заказ на нужную сумму и получите статусный ролл в подарок.
                Чем больше заказ — тем круче ролл!
              </p>
              <p className="text-xs text-[var(--text-tertiary)] mt-2 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5 flex-shrink-0" weight="fill" />
                Учитываются все товары кроме соусов, товаров из категории «Роллы -30%» и товаров из
                рулетки
              </p>
            </div>
          </div>
        </div>

        {/* Tier progress bar */}
        <div className="px-4 md:px-6">
          {/* Полоска прогресса — единственная ступень hairline (2) по контракту. */}
          <div className="flex items-center gap-1 h-2 rounded-hairline overflow-hidden bg-[var(--surface)]">
            {bonusRolls.map((roll, i) => (
              <div
                key={roll.id}
                className="flex-1 h-full rounded-hairline transition-all duration-500"
                style={{
                  backgroundColor: i <= activeIndex ? roll.color : 'var(--border-default)',
                  opacity: i <= activeIndex ? 1 : 0.3,
                }}
              />
            ))}
          </div>
          <div className="flex justify-between mt-2">
            {bonusRolls.map((roll) => (
              <span
                key={roll.id}
                className="t-nano font-bold uppercase tracking-wider"
                style={{ color: roll.color }}
              >
                {formatPrice(roll.threshold)}
              </span>
            ))}
          </div>
        </div>

        {/* Carousel */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <SpinnerGap className="w-8 h-8 text-[var(--primary)] animate-spin" />
          </div>
        ) : bonusRolls.length > 0 ? (
          <>
            <div>
              <div className="px-4 md:px-6 mb-2">
                <p className="text-xs font-semibold text-[var(--text-tertiary)] uppercase tracking-wider">
                  Выберите ролл — листайте
                </p>
              </div>
              <RollCarousel
                rolls={bonusRolls}
                activeIndex={activeIndex}
                onActiveChange={setActiveIndex}
                isDark={isDark}
              />
            </div>

            {/* Active roll details */}
            {activeRoll && <RollDetails roll={activeRoll} isDark={isDark} />}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-4">
            <Medal className="w-12 h-12 text-[var(--text-disabled)] mb-3" />
            <p className="text-sm text-[var(--text-tertiary)]">Роллы пока недоступны</p>
          </div>
        )}

        {/* How it works */}
        <div className="px-4 md:px-6">
          <div className="flex items-center gap-2 mb-3">
            <Info className="w-5 h-5 text-[var(--primary)]" weight="fill" />
            <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)]">
              Как это работает?
            </h3>
          </div>
          <div className="space-y-3">
            {[
              {
                step: '1',
                text: 'Добавьте блюда в корзину — при достижении суммы от 2 500 ₽ статусный ролл начислится автоматически',
              },
              {
                step: '2',
                text: 'Увеличивайте сумму заказа, чтобы получить ролл более высокого статуса: Silver → Gold → Platinum',
              },
              {
                step: '3',
                text: 'Статусный ролл добавится к вашему заказу бесплатно — никаких дополнительных действий не нужно!',
              },
            ].map((item) => (
              <div
                key={item.step}
                className="flex items-start gap-3 p-3 rounded-card bg-[var(--surface-secondary)] border border-[var(--border-default)]"
              >
                <div className="w-6 h-6 rounded-pill bg-[var(--primary)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-bold text-white">{item.step}</span>
                </div>
                <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Conditions */}
        <div className="px-4 md:px-6">
          <h3 className="text-base md:text-lg font-bold font-heading text-[var(--text-primary)] mb-3">
            Условия
          </h3>
          <div className="space-y-2">
            {(terms && terms.length > 0 ? terms : FALLBACK_TERMS).map((condition, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-pill bg-[var(--primary-subtle)] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <CheckCircle className="w-3 h-3 text-[var(--primary)]" weight="fill" />
                </div>
                <p className="text-sm md:text-base text-[var(--text-secondary)] leading-relaxed flex-1">
                  {condition}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mx-4 md:mx-6 p-4 md:p-6 rounded-card bg-gradient-to-r from-[var(--primary-subtle)] to-[var(--primary-muted)] border border-[var(--primary)]/20">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-card bg-[var(--primary)] flex items-center justify-center">
              <ShoppingCart className="w-6 h-6 text-white" weight="fill" />
            </div>
            <div>
              <p className="text-base md:text-lg text-[var(--text-primary)] font-bold mb-1">
                Соберите заказ прямо сейчас!
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                Добавьте блюда в корзину — статусный ролл добавится автоматически
              </p>
            </div>
          </div>
        </div>
      </div>
    </PromotionModal>
  );
}
